import {FC, useMemo, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useQuery} from 'react-query'
import {KTCard, KTCardBody} from '../../../../../_metronic/helpers'
import {
  buildPrinterStoryEvents,
  computePrinterKpis,
  correlateBlockedJobsWithMishaps,
  formatDuration,
  formatRelative,
  formatTimestamp,
  getDocsByDeviceId,
  getPrinterStatusDoc,
  PrinterIncidentGantt,
  StoryEventFeed,
  windowFromDays,
} from '../../print-story'

const WINDOW_OPTIONS = [
  {days: 1, label: '24h'},
  {days: 7, label: '7d'},
  {days: 30, label: '30d'},
]

export const PrinterStoryPage: FC = () => {
  const {deviceId = ''} = useParams()
  const [windowDays, setWindowDays] = useState(7)
  const window = useMemo(() => windowFromDays(windowDays), [windowDays])

  const statusQuery = useQuery(
    ['printer-story', 'status', deviceId],
    () => getPrinterStatusDoc(deviceId),
    {enabled: Boolean(deviceId), cacheTime: 0, refetchInterval: 30_000}
  )

  const mishapsQuery = useQuery(
    ['printer-story', 'mishaps', deviceId],
    () => getDocsByDeviceId('printerMishaps', deviceId, 'startedAtMs', 500, 'mishapId'),
    {enabled: Boolean(deviceId), cacheTime: 0}
  )

  const printJobsQuery = useQuery(
    ['printer-story', 'printJobs', deviceId],
    () => getDocsByDeviceId('printJobs', deviceId, 'publishedAtMs', 200, 'jobId'),
    {enabled: Boolean(deviceId), cacheTime: 0}
  )

  const commandLogsQuery = useQuery(
    ['printer-story', 'commandLogs', deviceId],
    () => getDocsByDeviceId('printerCommandLogs', deviceId, 'publishedAtMs', 100, 'logId'),
    {enabled: Boolean(deviceId), cacheTime: 0}
  )

  const callbackLogsQuery = useQuery(
    ['printer-story', 'callbackLogs', deviceId],
    () => getDocsByDeviceId('printerCallbackLogs', deviceId, 'receivedAtMs', 100, 'logId'),
    {enabled: Boolean(deviceId), cacheTime: 0}
  )

  const connectionLogsQuery = useQuery(
    ['printer-story', 'connectionLogs', deviceId],
    () => getDocsByDeviceId('printerConnectionLogs', deviceId, 'createdAtMs', 100, 'logId'),
    {enabled: Boolean(deviceId), cacheTime: 0}
  )

  const mishaps = mishapsQuery.data || []
  const printJobs = printJobsQuery.data || []
  const status = statusQuery.data

  const kpis = useMemo(() => computePrinterKpis(mishaps, window), [mishaps, window])
  const correlations = useMemo(
    () => correlateBlockedJobsWithMishaps(mishaps, printJobs),
    [mishaps, printJobs]
  )

  const events = useMemo(
    () =>
      buildPrinterStoryEvents({
        mishaps,
        printJobs,
        callbackLogs: callbackLogsQuery.data || [],
        commandLogs: commandLogsQuery.data || [],
        connectionLogs: connectionLogsQuery.data || [],
        includeConnectionLogs: true,
      }).filter((event) => event.ts >= window.startMs && event.ts <= window.endMs),
    [
      mishaps,
      printJobs,
      callbackLogsQuery.data,
      commandLogsQuery.data,
      connectionLogsQuery.data,
      window,
    ]
  )

  const severity = String(status?.severity || status?.normalizedStatus || 'info')
  const dashboardState = status?.dashboardState || status?.status || 'Unknown'
  const badgeClass =
    severity === 'normal'
      ? 'badge-light-success'
      : severity === 'attention'
      ? 'badge-light-warning'
      : severity === 'error' || severity === 'connectivity'
      ? 'badge-light-danger'
      : 'badge-light-primary'

  const loading =
    statusQuery.isLoading ||
    mishapsQuery.isLoading ||
    printJobsQuery.isLoading

  return (
    <KTCard>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='d-flex flex-column'>
            <Link
              to='/apps/printer-management/printers'
              className='text-muted fs-7 text-hover-primary mb-1'
            >
              ← Back to printers
            </Link>
            <h2 className='fw-bolder mb-0'>Printer Story · {deviceId}</h2>
            <div className='text-muted fs-7'>
              Incident history, uptime, and print correlation for this device.
            </div>
          </div>
        </div>
        <div className='card-toolbar'>
          <div className='btn-group'>
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option.days}
                type='button'
                className={`btn btn-sm ${
                  windowDays === option.days ? 'btn-primary' : 'btn-light'
                }`}
                onClick={() => setWindowDays(option.days)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <KTCardBody className='py-4'>
        {loading ? (
          <div className='text-muted fw-semibold py-10 text-center'>Loading printer story...</div>
        ) : (
          <>
            <div className='d-flex flex-wrap gap-4 mb-8'>
              <div className='border border-dashed rounded p-4 min-w-150px'>
                <div className='text-muted fs-7 fw-bold mb-2'>Current status</div>
                <span className={`badge ${badgeClass}`}>{String(dashboardState)}</span>
                <div className='text-muted fs-8 mt-2'>{severity}</div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-120px'>
                <div className='text-muted fs-7 fw-bold mb-2'>Uptime</div>
                <div className='fs-2 fw-bolder'>{kpis.uptimePercent.toFixed(1)}%</div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-120px'>
                <div className='text-muted fs-7 fw-bold mb-2'>Incidents</div>
                <div className='fs-2 fw-bolder'>{kpis.incidentCount}</div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-140px'>
                <div className='text-muted fs-7 fw-bold mb-2'>Downtime</div>
                <div className='fs-3 fw-bolder'>{formatDuration(kpis.totalDowntimeMs)}</div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-120px'>
                <div className='text-muted fs-7 fw-bold mb-2'>MTBF</div>
                <div className='fs-3 fw-bolder'>
                  {kpis.mtbfMs != null ? formatDuration(kpis.mtbfMs) : '—'}
                </div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-120px'>
                <div className='text-muted fs-7 fw-bold mb-2'>MTTR</div>
                <div className='fs-3 fw-bolder'>
                  {kpis.mttrMs != null ? formatDuration(kpis.mttrMs) : '—'}
                </div>
              </div>
              <div className='border border-dashed rounded p-4 min-w-180px'>
                <div className='text-muted fs-7 fw-bold mb-2'>Last incident</div>
                {kpis.lastIncident ? (
                  <>
                    <div className='fw-bold'>
                      {kpis.lastIncident.resolved ? 'Resolved' : 'Ongoing'}
                    </div>
                    <div className='text-muted fs-8'>
                      {formatRelative(kpis.lastIncident.startedAtMs)} ·{' '}
                      {formatTimestamp(kpis.lastIncident.startedAtMs)}
                    </div>
                    {kpis.lastIncident.reason && (
                      <div className='text-muted fs-8'>{kpis.lastIncident.reason}</div>
                    )}
                  </>
                ) : (
                  <div className='fw-bold'>None</div>
                )}
              </div>
            </div>

            {correlations.some((c) => c.blockedJobCount > 0) && (
              <div className='alert alert-light-warning mb-6'>
                <div className='fw-bold mb-1'>Print correlation</div>
                {correlations
                  .filter((c) => c.blockedJobCount > 0)
                  .slice(0, 5)
                  .map((c) => (
                    <div key={c.mishapId} className='fs-7'>
                      {c.blockedJobCount} print job(s) blocked during incident starting{' '}
                      {formatTimestamp(c.startedAtMs)}
                    </div>
                  ))}
              </div>
            )}

            <PrinterIncidentGantt
              mishaps={mishaps}
              printJobs={printJobs}
              window={window}
              correlations={correlations}
            />

            <div className='border border-dashed rounded p-5'>
              <div className='fw-bold text-gray-800 fs-5 mb-1'>Event feed</div>
              <div className='text-muted fs-7 mb-4'>
                Newest first. Log collections are limited (100–200 docs) for safety.
              </div>
              <StoryEventFeed events={events} />
            </div>
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}
